import { FavouritesIcon } from "@/components/icons/favourites-icon";
import { ScaleIcon } from "@/components/icons/scale-icon";
import { ShoppingCartIcon } from "@/components/icons/shopping-cart-icon";
import { TextButton } from "@/components/ui/buttons";
import { QueryKeys } from "@/constants/query-keys";
import { ShopListModal } from "@/features/shop-list/components/shop-list-modal";
import { WeightInputDialog, WeightInputDialogHandle } from "@/features/user-profile/components/weight-input-dialog";
import { useGetBodyStats } from "@/hooks/requests/body-status/use-get-body-stats";
import { useUpdateBodyStats } from "@/hooks/requests/body-status/use-update-body-stats";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { useIonRouter } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useRef, useState } from "react";

interface QuickAction {
  id: string;
  icon: React.ReactNode;
  label: string;
  path?: string;
  disabled?: boolean;
}

const quickActions: QuickAction[] = [
  {
    id: "your-foods",
    icon: <FavouritesIcon className="fill-white" />,
    label: "Foods Added",
    path: "/plans/saved-meals?tab=your-food",
  },
  {
    id: "shopping-list",
    icon: <ShoppingCartIcon />,
    label: "Shopping List",
  },
  {
    id: "update-weight",
    icon: <ScaleIcon />,
    label: "Update weight",
  },
];

/**
 * Grid of quick action buttons for common tasks
 */
export const QuickActionsGrid = memo(function QuickActionsGrid() {
  const [isWeightModalOpen, setIsWeightModalOpen] = useState(false);
  const [isShopListModalOpen, setIsShopListModalOpen] = useState(false);
  /** Ref attached to the grid container; used as the presenting element for the stacked modal */
  const gridRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<WeightInputDialogHandle>(null);
  const { bodyStats } = useGetBodyStats();
  const { updateBodyStatsAsync } = useUpdateBodyStats();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const router = useIonRouter();

  const weight = bodyStats?.currentWeight ? Number(bodyStats.currentWeight) : 0;
  const weightUnit = bodyStats?.currentWeightUnit || "kg";

  const handleActionClick = (action: QuickAction) => {
    if (action.id === "shopping-list") {
      setIsShopListModalOpen(true);
      return;
    }
    if (action.path) {
      router.push(action.path, "forward");
    } else if (action.id === "update-weight") {
      dialogRef.current?.focusInput();
      setIsWeightModalOpen(true);
    }
  };

  const handleWeightUpdate = async (value: string, unit: "kg" | "lbs") => {
    try {
      await updateBodyStatsAsync({
        height: bodyStats?.height || "170",
        heightUnit: bodyStats?.heightUnit || "cm",
        currentWeight: value,
        currentWeightUnit: unit,
        targetWeight: value,
        targetWeightUnit: unit,
        startingWeight: bodyStats?.startingWeight || value,
        startingWeightUnit: bodyStats?.startingWeightUnit || unit,
      });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.BODY_STATS] });
      showSuccess("Weight updated successfully");
    } catch (error) {
      showError("Failed to update weight");
      console.error(error);
    }
  };

  return (
    <div ref={gridRef} className="flex justify-around">
      {quickActions.map((action) => (
        <TextButton
          key={action.id}
          onClick={() => handleActionClick(action)}
          className="flex h-fit w-15 flex-col items-center gap-2"
          disabled={action.disabled}
        >
          <div className={cn("flex h-15 w-15 items-center justify-center rounded-full bg-black")}>
            {action.icon}
          </div>

          <span
            className={cn(
              textVariants.bodySmall,
              "text-center text-xs leading-tight font-medium text-gray-900 capitalize",
            )}
          >
            {action.label}
          </span>
        </TextButton>
      ))}
      <WeightInputDialog
        ref={dialogRef}
        isOpen={isWeightModalOpen}
        onDismiss={() => setIsWeightModalOpen(false)}
        defaultValue={weight.toString()}
        defaultUnit={weightUnit}
        onSelect={handleWeightUpdate}
      />

      <ShopListModal
        isOpen={isShopListModalOpen}
        onDismiss={() => setIsShopListModalOpen(false)}
        pageRef={gridRef as React.RefObject<HTMLElement>}
      />
    </div>
  );
});
